import { ApiError } from "@/core/api/types";
import type { Message, MessagePage } from "./types";

export const mergeMessages = (current: Message[], incoming: Message[]) => {
  const byId = new Map(current.map((message) => [message.id, message]));
  for (const message of incoming)
    byId.set(message.id, { ...byId.get(message.id), ...message });
  return Array.from(byId.values()).sort((a, b) => a.sequence - b.sequence);
};

export const mergeMessagePage = (
  current: MessagePage | undefined,
  page: MessagePage,
): MessagePage => ({
  ...page,
  messages: mergeMessages(current?.messages ?? [], [
    ...page.messages,
    ...(page.latest_request ? [page.latest_request] : []),
  ]),
});

export const errorStatus = (error: unknown) =>
  error instanceof ApiError ? error.status : undefined;
export const isAccessError = (error: unknown) =>
  [401, 403].includes(errorStatus(error) ?? 0);

export const editorError = (error: unknown) => {
  switch (errorStatus(error)) {
    case 400:
      return "Check the request or file details, then try again.";
    case 401:
      return "Your session expired. Sign in again to continue.";
    case 403:
      return "Payroll rules editing is no longer available for your account. Contact a manager at your business or Volca.";
    case 404:
      return "These rules are no longer available. Open the current rules to continue.";
    case 409:
      return "The workspace changed or another request is active. Review the latest rules and progress before you try again.";
    case 422:
      return "The request or file could not be accepted. Check it and try again.";
    case 503:
      return "Your request was saved, but could not start. Use Retry to start the same request.";
    default:
      return "The connection failed. Retry to check the saved request before you send another one.";
  }
};

/**
 * The refusals the API writes for the owner to read, rather than for us.
 *
 * Each one is a sentence about their own proposal: nothing to publish, the
 * comparison has not been run, a request is still going. The code is what
 * marks them, not the status. Reading the status instead kept the 409s and
 * threw away every publish refusal, which is a 422, so an owner who published
 * too early was told "the request could not be accepted" instead of what to
 * do about it.
 */
const OWNER_FACING_CODES = [
  "publish_refused",
  "editor_busy",
  "comparison_unavailable",
  "no_pay_period",
] as const;

type OwnerFacingCode = (typeof OWNER_FACING_CODES)[number];

const ownerFacingMessage = (error: unknown) => {
  if (!(error instanceof ApiError) || !error.message.trim()) return undefined;
  const code = (error.data as { code?: string } | undefined)?.code;
  return OWNER_FACING_CODES.includes(code as OwnerFacingCode)
    ? error.message
    : undefined;
};

/** Says what the API said when the API wrote it for the owner. */
export const proposalError = (error: unknown) =>
  ownerFacingMessage(error) ?? editorError(error);

/**
 * True only when the business has no pay period to compare over.
 *
 * This is the one answer that lets a proposal publish without a comparison, so
 * it has its own code. `comparison_unavailable` covers a sheet that is not
 * ready and rules that are missing as well, and treating those as "cannot be
 * compared" dropped the gate on a proposal that simply had not been run yet.
 */
export const comparisonImpossible = (error: unknown) =>
  error instanceof ApiError &&
  (error.data as { code?: string } | undefined)?.code === "no_pay_period";
